import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import React from "react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * A React component that renders parts of a translated text with styling and event handlers.
 *
 * @example
 * <Render
 *   text={t("loyalty.accept_terms")}
 *   props={{
 *     className: "font-medium text-takyon-600 hover:underline",
 *     onClick: () => window.open('https://takyon.io/terms', '_blank')
 *   }}
 * />
 */
export function Render({
  text,
  props,
}: {
  text: string;
  props: React.HTMLAttributes<HTMLSpanElement>;
}): JSX.Element {
  if (!text) return React.createElement("span", null);

  // Split the text by {{ and }}
  const parts = text.split(/{{|}}/);

  // Create elements array
  const elements: React.ReactNode[] = [];

  // Process each part
  parts.forEach((part, index) => {
    // Even indices are regular text, odd indices are highlighted parts
    if (index % 2 === 0) {
      if (part) elements.push(part);
    } else {
      // This is a highlighted part, apply the props
      elements.push(
        React.createElement(
          "span",
          { key: `highlight-${index}`, ...props },
          part
        )
      );
    }
  });

  return React.createElement(React.Fragment, null, ...elements);
}

type MatchResult = {
  score: number;
  matchedIndexes: number[];
};

export function fuzzyMatch(
  pattern: string,
  target: string
): MatchResult | null {
  const patternLower = pattern.toLowerCase();
  const targetLower = target.toLowerCase();

  let score = 0;
  const matchedIndexes: number[] = [];

  let patternIndex = 0;
  let targetIndex = 0;
  let consecutive = 0;

  while (patternIndex < pattern.length && targetIndex < target.length) {
    const patternChar = patternLower[patternIndex];
    const targetChar = targetLower[targetIndex];

    if (patternChar === targetChar) {
      // Bonus points
      let bonus = 10;

      if (targetIndex === 0 || /[\s-_A-Z]/.test(target[targetIndex - 1])) {
        bonus += 15; // match at start of word or camelCase hump
      }

      if (target[targetIndex] === pattern[patternIndex]) {
        bonus += 5; // case-sensitive bonus
      }

      consecutive++;
      bonus += consecutive * 5; // reward consecutive matches
      score += bonus;

      matchedIndexes.push(targetIndex);
      patternIndex++;
    } else {
      consecutive = 0; // reset
    }

    targetIndex++;
  }

  if (patternIndex !== pattern.length) {
    return null; // not all characters matched
  }

  return {
    score,
    matchedIndexes,
  };
}
